import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import ImageModal from './ImageModal'; // Import the new modal component
import { trackEvent } from '../utils/analytics';
import InfoChip from './InfoChip';

function PropertyForm() {
  const { id } = useParams(); // For editing, property ID will be in URL
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    address: '',
    unit: '',
    city: '',
    province: '',
    postal_code: '',
    rent: '',
    bedrooms: '',
    bathrooms: '',
    property_type: '',
    amenities: '',
    description: '',
    status: 'available',
    timezone: 'America/Toronto',
  });
  const [propertySettings, setPropertySettings] = useState({
    payment_method: '',
    utilities_included: {
      water: false,
      heat: false,
      hydro: false,
      internet: false,
      notes: ''
    },
    parking_included: false,
    parking_details: '',
    smoking_allowed: '',
    appliances: '',
    furnishings: '',
    additional_terms: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [verification, setVerification] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [savedNeedsPublish, setSavedNeedsPublish] = useState(null); // { propertyId, profileComplete, identityDone }
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null); // State for the modal

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_API_BASE_URL}${url}`;
  };
  const [dragIndex, setDragIndex] = useState(null);
  const [subscription, setSubscription] = useState({ subscription_status: 'free' });
  const [propertyCount, setPropertyCount] = useState(0);
  const [propertyLocked, setPropertyLocked] = useState(false);
  const [walkthroughSlots, setWalkthroughSlots] = useState([]);
  const [addingSlots, setAddingSlots] = useState([]);
  const today = new Date().toISOString().split('T')[0];
  const [gridDate, setGridDate] = useState(today);
  const [gridStartTime, setGridStartTime] = useState('06:00');
  const [gridEndTime, setGridEndTime] = useState('23:00');
  const [gridInterval, setGridInterval] = useState(30);
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringDay, setRecurringDay] = useState('SAT');
  const [recurringWeeks, setRecurringWeeks] = useState(4);
  const [addingRecurring, setAddingRecurring] = useState(false);
  const [showViewingForm, setShowViewingForm] = useState(false);
  const [expandedSlotDates, setExpandedSlotDates] = useState({});
  const schedulingRef = useRef(null);


  const schedulingOnly = new URLSearchParams(location.search).get('focus') === 'walkthrough';
  const canSchedule = isEditMode ? formData.status === 'available' : true;

  const flowSteps = [
    { key: 'basics', label: 'Basics' },
    { key: 'details', label: 'Details' },
    { key: 'photos', label: 'Photos' },
    { key: 'review', label: isEditMode ? 'Save' : 'Publish' },
    ...(isEditMode && canSchedule ? [{ key: 'scheduling', label: 'Viewings' }] : []),
  ];
  const [activeFlowStep, setActiveFlowStep] = useState(schedulingOnly ? 'scheduling' : 'basics');

  const focusFlowStep = (key) => {
    setActiveFlowStep(key);
  };

  useEffect(() => {
    if (schedulingOnly) {
      setActiveFlowStep('scheduling');
      setTimeout(() => {
        schedulingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [schedulingOnly]);
  const handleNextStep = () => {
    const currentIndex = flowSteps.findIndex((s) => s.key === activeFlowStep);
    if (currentIndex < flowSteps.length - 1) {
      setActiveFlowStep(flowSteps[currentIndex + 1].key);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const [leaseDraft, setLeaseDraft] = useState({
    preselect: {
      isCondo: false,
      parkingIncluded: false,
      utilitiesIncluded: 'none',
      laundry: 'none',
      furnished: false,
      smokingAllowed: 'no',
      rentDiscount: false,
      rentDeposit: true,
      paymentMethod: 'pad',
      otherServices: false,
      specialTerms: false
    },
    essentials: {
      rentDueDay: '1st',
      tenancyType: 'monthly',
      rentPayableTo: '',
      notes: ''
    },
    conditional: {
      parkingDetails: '',
      utilities: {
        water: false,
        heat: false,
        hydro: false,
        internet: false,
        gas: false,
        ac: false,
        storage: false,
        guestParking: false,
        notes: ''
      },
      laundryDetails: '',
      furnishedItems: '',
      smokingRules: '',
      discountDetails: '',
      depositAmount: '',
      otherServicesDetails: '',
      specialTermsText: ''
    },
    meta: {
      readyOverride: false
    }
  });

  useEffect(() => {
    const fetchPropertyForEdit = async () => {
      if (id) {
        setIsEditMode(true);
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.status === 402) {
            setPropertyLocked(true);
            setLoading(false);
            return;
          }
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          setPropertyLocked(false);
          const data = await response.json();
          setFormData({
            address: data.property.address || '',
            unit: data.property.unit || '',
            city: data.property.city || '',
            province: data.property.province || '',
            postal_code: data.property.postal_code || '',
            rent: data.property.rent || '',
            bedrooms: data.property.bedrooms || '',
            bathrooms: data.property.bathrooms || '',
            property_type: data.property.property_type || '',
            amenities: data.property.amenities || '',
            description: data.property.description || '',
            status: data.property.status || 'available',
            timezone: data.property.timezone || 'America/Toronto',
          });
          setUploadedImageUrls(data.property.photo_urls || []);

          const settingsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${id}/settings`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          const settingsData = settingsRes.ok ? await settingsRes.json() : { settings: null };
          if (settingsRes.ok && settingsData.settings) {
            setPropertySettings({
              payment_method: settingsData.settings.payment_method || '',
              utilities_included: normalizeUtilities(settingsData.settings.utilities_included || {}),
              parking_included: settingsData.settings.parking_included || false,
              parking_details: settingsData.settings.parking_details || '',
              smoking_allowed: settingsData.settings.smoking_allowed === true ? 'yes' : settingsData.settings.smoking_allowed === false ? 'no' : '',
              appliances: settingsData.settings.appliances || '',
              furnishings: settingsData.settings.furnishings || '',
              additional_terms: settingsData.settings.additional_terms || ''
            });

            if (settingsData.settings.lease_wizard) {
              const wizard = settingsData.settings.lease_wizard || {};
              setLeaseDraft((prev) => ({
                preselect: { ...prev.preselect, ...(wizard.preselect || {}) },
                essentials: { ...prev.essentials, ...(wizard.essentials || {}) },
                conditional: { ...prev.conditional, ...(wizard.conditional || {}) },
                meta: { ...prev.meta, ...(wizard.meta || {}) }
              }));
            }
          }

          const slotsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${id}/walkthrough-slots`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const slotsData = slotsRes.ok ? await slotsRes.json() : { slots: [] };
          if (slotsRes.ok) {
            setWalkthroughSlots(slotsData.slots || []);
          }
        } catch (err) {
          setError(err.message);
          console.error('Error fetching property for edit:', err);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchPropertyForEdit();
  }, [id, navigate]);

  useEffect(() => {
    if (schedulingOnly) {
      setActiveFlowStep('review');
    }
  }, [schedulingOnly]);

  useEffect(() => {
    const fetchSubscription = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setSubscription(data.subscription || { subscription_status: 'free' });
          setPropertyCount(data.propertyCount || 0);
        }
      } catch (err) {
        console.error('Subscription fetch error:', err);
      }
    };
    fetchSubscription();
  }, []);

  // Gate: redirect free plan landlords with 1+ properties away from creation
  useEffect(() => {
    if (!id && subscription && propertyCount > 0 && subscription.subscription_status !== 'active') {
      navigate('/landlord/dashboard/v2', { replace: true, state: { upgradeRequired: true } });
    }
  }, [id, subscription, propertyCount, navigate]);

  useEffect(() => {
    const fetchVerification = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/verification/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setVerification(data.verification || null);
        }
      } catch (err) {
        // silent
      } finally {
        setVerificationLoading(false);
      }
    };
    fetchVerification();
  }, []);

  const normalizeUtilities = (utilities = {}) => ({
    water: false,
    heat: false,
    hydro: false,
    internet: false,
    notes: '',
    ...utilities
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };


  const handleLeaseDraftChange = (section, field, value) => {
    setLeaseDraft((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleLeaseDraftUtilitiesChange = (field, value) => {
    setLeaseDraft((prev) => ({
      ...prev,
      conditional: {
        ...prev.conditional,
        utilities: {
          ...prev.conditional.utilities,
          [field]: value
        }
      }
    }));
  };


  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleImageUpload(files);
    }
  };

  const handleImageUpload = async (files) => {
    const remainingSlots = 10 - uploadedImageUrls.length;
    if (remainingSlots <= 0) {
      setError('Maximum 10 images per listing.');
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    if (filesToUpload.length < files.length) {
      setError(`Only ${remainingSlots} more image${remainingSlots === 1 ? '' : 's'} can be added (max 10).`);
    }

    const uploadFormData = new FormData();
    filesToUpload.forEach(file => {
      uploadFormData.append('images', file);
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/upload-images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Image upload failed');
      }

      setUploadedImageUrls(prevUrls => [...prevUrls, ...data.imageUrls]);
      // Clear the file input for the next selection if needed
      document.getElementById('image_upload').value = '';
    } catch (err) {
      setError(`Image upload error: ${err.message}`);
      console.error('Image upload error:', err);
    }
  };

  const handleRemoveImage = (urlToRemove) => {
    setUploadedImageUrls(prevUrls => prevUrls.filter(url => url !== urlToRemove));
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    setUploadedImageUrls((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(dragIndex, 1);
      copy.splice(index, 0, moved);
      return copy;
    });
    setDragIndex(null);
  };

  const moveImage = (index, direction) => {
    setUploadedImageUrls((prev) => {
      const copy = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= copy.length) return prev;
      const [moved] = copy.splice(index, 1);
      copy.splice(newIndex, 0, moved);
      return copy;
    });
  };

  const toLocalSlotKey = (dateValue) => {
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hours}:${minutes}`;
  };

  const buildGridSlots = () => {
    if (!gridDate || !gridStartTime || !gridEndTime) return [];
    const start = new Date(`${gridDate}T${gridStartTime}`);
    const end = new Date(`${gridDate}T${gridEndTime}`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return [];
    const slots = [];
    const current = new Date(start);
    while (current < end) {
      const hours = String(current.getHours()).padStart(2, '0');
      const minutes = String(current.getMinutes()).padStart(2, '0');
      slots.push(`${gridDate}T${hours}:${minutes}`);
      current.setMinutes(current.getMinutes() + Number(gridInterval || 30));
    }
    return slots;
  };

  const handleAddGridSlot = async (slotKey) => {
    if (!isEditMode) return;
    if (addingSlots.includes(slotKey)) return;
    const token = localStorage.getItem('token');
    setAddingSlots((prev) => [...prev, slotKey]);
    try {
      const isoStart = new Date(slotKey).toISOString();
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${id}/walkthrough-slots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ start_time: isoStart })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to add slot');
      setWalkthroughSlots((prev) => [...prev, data.slot]);
    } catch (err) {
      setError(err.message || 'Failed to add walkthrough slot');
    } finally {
      setAddingSlots((prev) => prev.filter((slot) => slot !== slotKey));
    }
  };

  const handleDeleteSlot = async (slotId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/walkthrough-slots/${slotId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete slot');
      setWalkthroughSlots(prev => prev.filter(slot => slot.id !== slotId));
    } catch (err) {
      setError(err.message || 'Failed to delete slot');
    }
  };

  const handleAddRecurringSlots = async () => {
    if (!isEditMode || addingRecurring) return;
    const token = localStorage.getItem('token');
    setAddingRecurring(true);
    setError(null);
    try {
      const dayMap = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
      const targetDay = dayMap[recurringDay];
      const startH = parseInt(gridStartTime.split(':')[0]);
      const startM = parseInt(gridStartTime.split(':')[1]);
      const endH = parseInt(gridEndTime.split(':')[0]);
      const endM = parseInt(gridEndTime.split(':')[1]);
      const interval = Number(gridInterval || 30);

      // Find next occurrence of target day
      const now = new Date();
      let cursor = new Date(now);
      cursor.setHours(0, 0, 0, 0);
      while (cursor.getDay() !== targetDay) {
        cursor.setDate(cursor.getDate() + 1);
      }

      const slotsToAdd = [];
      for (let week = 0; week < recurringWeeks; week++) {
        const dayDate = new Date(cursor);
        dayDate.setDate(dayDate.getDate() + week * 7);
        let h = startH, m = startM;
        while (h < endH || (h === endH && m < endM)) {
          const slotDate = new Date(dayDate);
          slotDate.setHours(h, m, 0, 0);
          if (slotDate > now) {
            slotsToAdd.push(slotDate.toISOString());
          }
          m += interval;
          if (m >= 60) { h += Math.floor(m / 60); m = m % 60; }
        }
      }

      if (slotsToAdd.length === 0) {
        setError('No future slots to add with these settings.');
        return;
      }

      // Add slots sequentially to avoid overwhelming the server
      const added = [];
      for (const isoStart of slotsToAdd) {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${id}/walkthrough-slots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ start_time: isoStart })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.slot) added.push(data.slot);
      }
      setWalkthroughSlots(prev => [...prev, ...added]);
    } catch (err) {
      setError(err.message || 'Failed to add recurring slots');
    } finally {
      setAddingRecurring(false);
    }
  };

  const isUnverified = !verificationLoading && (!verification || (verification.id_status !== 'verified' && verification.ownership_status !== 'approved'));


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');

    if (!token || userType !== 'landlord') {
      alert('You must be logged in as a landlord to manage properties.');
      navigate('/login');
      return;
    }

    // The `uploadedImageUrls` state is now the single source of truth
    const wantedToPublish = formData.status === 'available' && isUnverified;
    const payload = {
      ...formData,
      status: wantedToPublish ? 'off_market' : formData.status,
      rent: parseFloat(formData.rent),
      bedrooms: parseInt(formData.bedrooms),
      bathrooms: parseFloat(formData.bathrooms),
      photo_urls: uploadedImageUrls,
    };

    try {
      const url = isEditMode ? `${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${id}` : `${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 402) {
        if (!isEditMode) {
          // Free plan limit reached during creation — redirect to pricing
          alert(data.error || 'Free plan allows 1 property. Upgrade to Pro to add more.');
          navigate('/pricing');
          return;
        }
        setPropertyLocked(true);
        throw new Error(data.error || 'Upgrade required to edit this property.');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Operation failed');
      }

      if (data.subscription_required) {
        alert('You’re on the free plan (1 property limit). This listing was saved as Draft. Upgrade to add more.');
      }

      const propertyId = data.property?.id || id;
      if (propertyId) {
        const rentDueNumeric = Number.parseInt(String(leaseDraft.essentials.rentDueDay || '').replace(/\D/g, ''), 10);
        const missingLeaseItems = getLeaseMissingItems();
        const leaseReady = missingLeaseItems.length === 0 || leaseDraft.meta.readyOverride;
        const leaseReadyState = leaseReady
          ? 'ready'
          : missingLeaseItems.length === 0
            ? 'draft'
            : 'incomplete';

        const settingsPayload = {
          payment_method: leaseDraft.preselect.paymentMethod || null,
          rent_payable_to: leaseDraft.essentials.rentPayableTo || null,
          rent_due_day: Number.isNaN(rentDueNumeric) ? null : rentDueNumeric,
          tenancy_type: leaseDraft.essentials.tenancyType || null,
          utilities_included: leaseDraft.conditional.utilities || {},
          parking_included: leaseDraft.preselect.parkingIncluded,
          parking_details: leaseDraft.conditional.parkingDetails || null,
          smoking_allowed: leaseDraft.preselect.smokingAllowed === 'yes' ? true : leaseDraft.preselect.smokingAllowed === 'no' ? false : null,
          appliances: propertySettings.appliances,
          furnishings: leaseDraft.conditional.furnishedItems || null,
          additional_terms: leaseDraft.conditional.specialTermsText || null,
          lease_wizard: leaseDraft,
          lease_ready: leaseReady,
          lease_ready_state: leaseReadyState
        };

        const settingsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${propertyId}/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(settingsPayload),
        });
        if (!settingsRes.ok) {
          const settingsData = await settingsRes.json().catch(() => ({}));
          throw new Error(settingsData.error || 'Failed to save property settings');
        }
      }

      trackEvent({
        event_name: isEditMode ? 'property_updated' : 'property_created',
        event_category: 'property',
        event_action: isEditMode ? 'update' : 'create',
        property_id: data.property?.id,
        metadata: { status: formData.status }
      });

      if (!isEditMode && window.fbq) {
        window.fbq('track', 'Lead');
      }

      if (wantedToPublish && propertyId) {
        // Check what's missing for publishing
        let profileComplete = false;
        try {
          const obRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/onboarding-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (obRes.ok) {
            const obData = await obRes.json();
            profileComplete = !!obData.profile_complete;
          }
        } catch (e) { /* silent */ }
        const identityDone = verification?.id_status === 'verified' || verification?.id_status === 'id_verified';
        setSavedNeedsPublish({ propertyId, profileComplete, identityDone });
        return;
      }

      alert(isEditMode ? 'Property updated successfully!' : 'Property published! You can now set up viewing times.');
      if (!isEditMode && propertyId) {
        navigate(`/landlord/properties/edit/${propertyId}?focus=walkthrough`);
      } else {
        navigate('/landlord/dashboard/v2');
      }
    } catch (err) {
      setError(err.message);
      console.error(`Property ${isEditMode ? 'update' : 'create'} error:`, err);
    }
  };

  const getLeaseMissingItems = () => {
    const missing = [];
    if (!formData.address?.trim()) missing.push('Property address');
    if (!formData.city?.trim()) missing.push('City');
    if (!formData.postal_code?.trim()) missing.push('Postal code');
    if (!formData.rent) missing.push('Rent amount');
    if (!leaseDraft.essentials.rentDueDay) missing.push('Rent due day');
    if (!leaseDraft.essentials.tenancyType) missing.push('Tenancy type');
    if (!leaseDraft.essentials.rentPayableTo) missing.push('Rent payable to');
    return missing;
  };

  const renderLeaseConditionalDetails = () => {
    const hasConditionals = leaseDraft.preselect.parkingIncluded
      || leaseDraft.preselect.utilitiesIncluded !== 'none'
      || leaseDraft.preselect.laundry !== 'none'
      || leaseDraft.preselect.furnished
      || leaseDraft.preselect.smokingAllowed === 'yes'
      || leaseDraft.preselect.rentDiscount
      || leaseDraft.preselect.rentDeposit
      || leaseDraft.preselect.otherServices
      || leaseDraft.preselect.specialTerms;

    if (!hasConditionals) {
      return <p className="status-text">No extra details selected.</p>;
    }

    return (
      <div className="lease-wizard-conditional">
        {leaseDraft.preselect.parkingIncluded && (
          <label>
            Parking details
            <input type="text" value={leaseDraft.conditional.parkingDetails} onChange={(e) => handleLeaseDraftChange('conditional', 'parkingDetails', e.target.value)} placeholder="e.g., 1 underground spot (P2 #47)" />
          </label>
        )}
        {leaseDraft.preselect.utilitiesIncluded !== 'none' && (
          <div className="lease-wizard-utilities">
            <span>Utilities included</span>
            <div className="lease-wizard-checkboxes">
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.water} onChange={(e) => handleLeaseDraftUtilitiesChange('water', e.target.checked)} /> Water</label>
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.heat} onChange={(e) => handleLeaseDraftUtilitiesChange('heat', e.target.checked)} /> Heat</label>
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.hydro} onChange={(e) => handleLeaseDraftUtilitiesChange('hydro', e.target.checked)} /> Hydro</label>
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.internet} onChange={(e) => handleLeaseDraftUtilitiesChange('internet', e.target.checked)} /> Internet</label>
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.gas} onChange={(e) => handleLeaseDraftUtilitiesChange('gas', e.target.checked)} /> Gas</label>
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.ac} onChange={(e) => handleLeaseDraftUtilitiesChange('ac', e.target.checked)} /> A/C</label>
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.storage} onChange={(e) => handleLeaseDraftUtilitiesChange('storage', e.target.checked)} /> Storage</label>
              <label><input type="checkbox" checked={leaseDraft.conditional.utilities.guestParking} onChange={(e) => handleLeaseDraftUtilitiesChange('guestParking', e.target.checked)} /> Guest parking</label>
            </div>
            <input type="text" value={leaseDraft.conditional.utilities.notes} onChange={(e) => handleLeaseDraftUtilitiesChange('notes', e.target.value)} placeholder="Utilities notes (optional)" />
          </div>
        )}
        {leaseDraft.preselect.laundry !== 'none' && (
          <label>
            Laundry details
            <input type="text" value={leaseDraft.conditional.laundryDetails} onChange={(e) => handleLeaseDraftChange('conditional', 'laundryDetails', e.target.value)} placeholder="Location or pay-per-use details" />
          </label>
        )}
        {leaseDraft.preselect.furnished && (
          <label>
            Furnished items
            <textarea value={leaseDraft.conditional.furnishedItems} onChange={(e) => handleLeaseDraftChange('conditional', 'furnishedItems', e.target.value)} rows="2" placeholder="Beds, sofa, dining table, etc." />
          </label>
        )}
        {leaseDraft.preselect.smokingAllowed === 'yes' && (
          <label>
            Smoking rules
            <textarea value={leaseDraft.conditional.smokingRules} onChange={(e) => handleLeaseDraftChange('conditional', 'smokingRules', e.target.value)} rows="2" placeholder="Where smoking is permitted" />
          </label>
        )}
        {leaseDraft.preselect.rentDiscount && (
          <label>
            Discount description
            <textarea value={leaseDraft.conditional.discountDetails} onChange={(e) => handleLeaseDraftChange('conditional', 'discountDetails', e.target.value)} rows="2" placeholder="e.g., $100 off for first 3 months" />
          </label>
        )}
        {leaseDraft.preselect.otherServices && (
          <label>
            Other services included
            <textarea value={leaseDraft.conditional.otherServicesDetails} onChange={(e) => handleLeaseDraftChange('conditional', 'otherServicesDetails', e.target.value)} rows="2" placeholder="Storage, guest parking, etc." />
          </label>
        )}
        {leaseDraft.preselect.specialTerms && (
          <label>
            Special terms
            <textarea value={leaseDraft.conditional.specialTermsText} onChange={(e) => handleLeaseDraftChange('conditional', 'specialTermsText', e.target.value)} rows="3" placeholder="House rules, condo by-laws, etc." />
          </label>
        )}
      </div>
    );
  };

  const renderLeaseWizardStep = (step) => {
    if (step === 1) {
      return (
        <div>
          <div className="form-group">
            <label>Condo unit?</label>
            <select value={leaseDraft.preselect.isCondo ? 'yes' : 'no'} onChange={(e) => handleLeaseDraftChange('preselect', 'isCondo', e.target.value === 'yes')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label>Parking included?</label>
            <select value={leaseDraft.preselect.parkingIncluded ? 'yes' : 'no'} onChange={(e) => handleLeaseDraftChange('preselect', 'parkingIncluded', e.target.value === 'yes')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label>Utilities included</label>
            <select value={leaseDraft.preselect.utilitiesIncluded} onChange={(e) => handleLeaseDraftChange('preselect', 'utilitiesIncluded', e.target.value)}>
              <option value="none">None</option>
              <option value="some">Some</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="form-group">
            <label>Laundry</label>
            <select value={leaseDraft.preselect.laundry} onChange={(e) => handleLeaseDraftChange('preselect', 'laundry', e.target.value)}>
              <option value="none">None</option>
              <option value="free">Free</option>
              <option value="pay">Pay per use</option>
            </select>
          </div>
          <div className="form-group">
            <label>Furnished?</label>
            <select value={leaseDraft.preselect.furnished ? 'yes' : 'no'} onChange={(e) => handleLeaseDraftChange('preselect', 'furnished', e.target.value === 'yes')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label>Smoking allowed?</label>
            <select value={leaseDraft.preselect.smokingAllowed} onChange={(e) => handleLeaseDraftChange('preselect', 'smokingAllowed', e.target.value)}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label>Rent discount?</label>
            <select value={leaseDraft.preselect.rentDiscount ? 'yes' : 'no'} onChange={(e) => handleLeaseDraftChange('preselect', 'rentDiscount', e.target.value === 'yes')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label>Last month's rent deposit?</label>
            <select value={leaseDraft.preselect.rentDeposit ? 'yes' : 'no'} onChange={(e) => handleLeaseDraftChange('preselect', 'rentDeposit', e.target.value === 'yes')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label>Other services?</label>
            <select value={leaseDraft.preselect.otherServices ? 'yes' : 'no'} onChange={(e) => handleLeaseDraftChange('preselect', 'otherServices', e.target.value === 'yes')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label>Special terms?</label>
            <select value={leaseDraft.preselect.specialTerms ? 'yes' : 'no'} onChange={(e) => handleLeaseDraftChange('preselect', 'specialTerms', e.target.value === 'yes')}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="lease-wizard-grid">
          <label>
            Rent due day
            <input type="text" value={leaseDraft.essentials.rentDueDay} onChange={(e) => handleLeaseDraftChange('essentials', 'rentDueDay', e.target.value)} placeholder="e.g., 1st" />
          </label>
          <label>
            Tenancy type
            <select value={leaseDraft.essentials.tenancyType} onChange={(e) => handleLeaseDraftChange('essentials', 'tenancyType', e.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="fixed">Fixed term</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label>
            Rent payable to
            <input type="text" value={leaseDraft.essentials.rentPayableTo} onChange={(e) => handleLeaseDraftChange('essentials', 'rentPayableTo', e.target.value)} placeholder="Default to landlord legal name" />
          </label>
          <label>
            Notes (optional)
            <textarea value={leaseDraft.essentials.notes} onChange={(e) => handleLeaseDraftChange('essentials', 'notes', e.target.value)} rows="3" />
          </label>
        </div>
      );
    }

    if (step === 3) {
      return renderLeaseConditionalDetails();
    }

    return null;
  };

  const renderUpdateAction = () => {
    if (!isEditMode) return null;
    return (
      <div className="form-actions" style={{ marginTop: '1.5rem' }}>
        <button type="submit" className="primary-button">Update Listing</button>
      </div>
    );
  };

  const renderLeaseReviewSummary = () => {
    const missingItems = getLeaseMissingItems();
    const isReady = missingItems.length === 0;

    return (
      <div className="lease-wizard-review">
        <div>
          <strong>Quick fit:</strong>
          <ul>
            <li>Condo: {leaseDraft.preselect.isCondo ? 'Yes' : 'No'}</li>
            <li>Parking: {leaseDraft.preselect.parkingIncluded ? 'Yes' : 'No'}</li>
            <li>Utilities: {leaseDraft.preselect.utilitiesIncluded}</li>
            <li>Laundry: {leaseDraft.preselect.laundry}</li>
            <li>Furnished: {leaseDraft.preselect.furnished ? 'Yes' : 'No'}</li>
            <li>Smoking allowed: {leaseDraft.preselect.smokingAllowed}</li>
            <li>Discount: {leaseDraft.preselect.rentDiscount ? 'Yes' : 'No'}</li>
            <li>Last month's rent deposit: {leaseDraft.preselect.rentDeposit ? 'Yes' : 'No'}</li>
            <li>Payment method: {leaseDraft.preselect.paymentMethod}</li>
            <li>Other services: {leaseDraft.preselect.otherServices ? 'Yes' : 'No'}</li>
            <li>Special terms: {leaseDraft.preselect.specialTerms ? 'Yes' : 'No'}</li>
          </ul>
        </div>
        <div>
          <strong>Essentials:</strong>
          <ul>
            <li>Rent due day: {leaseDraft.essentials.rentDueDay || '—'}</li>
            <li>Tenancy type: {leaseDraft.essentials.tenancyType}</li>
            <li>Rent payable to: {leaseDraft.essentials.rentPayableTo || '—'}</li>
          </ul>
        </div>
        <div>
          <strong>Conditional:</strong>
          <ul>
            {leaseDraft.preselect.parkingIncluded && <li>Parking: {leaseDraft.conditional.parkingDetails || 'Yes'}</li>}
            {leaseDraft.preselect.utilitiesIncluded !== 'none' && (
              <li>Utilities: {Object.entries(leaseDraft.conditional.utilities)
                .filter(([key, val]) => key !== 'notes' && val)
                .map(([key]) => key)
                .join(', ') || leaseDraft.preselect.utilitiesIncluded}</li>
            )}
            {leaseDraft.preselect.laundry !== 'none' && <li>Laundry: {leaseDraft.conditional.laundryDetails || leaseDraft.preselect.laundry}</li>}
            {leaseDraft.preselect.furnished && <li>Furnished: {leaseDraft.conditional.furnishedItems || 'Yes'}</li>}
            {leaseDraft.preselect.smokingAllowed === 'yes' && <li>Smoking rules: {leaseDraft.conditional.smokingRules || 'Yes'}</li>}
            {leaseDraft.preselect.rentDiscount && <li>Discount: {leaseDraft.conditional.discountDetails || 'Yes'}</li>}
            {leaseDraft.preselect.rentDeposit && <li>Last month's rent deposit: {leaseDraft.conditional.depositAmount ? `$${leaseDraft.conditional.depositAmount}` : 'Yes'}</li>}
            {leaseDraft.preselect.otherServices && <li>Other services: {leaseDraft.conditional.otherServicesDetails || 'Yes'}</li>}
            {leaseDraft.preselect.specialTerms && <li>Special terms: {leaseDraft.conditional.specialTermsText || 'Yes'}</li>}
            {!leaseDraft.preselect.parkingIncluded && leaseDraft.preselect.utilitiesIncluded === 'none' && leaseDraft.preselect.laundry === 'none' && !leaseDraft.preselect.furnished && leaseDraft.preselect.smokingAllowed !== 'yes' && !leaseDraft.preselect.rentDiscount && !leaseDraft.preselect.rentDeposit && !leaseDraft.preselect.otherServices && !leaseDraft.preselect.specialTerms && (
              <li>No extra details selected.</li>
            )}
          </ul>
        </div>
        <div className="lease-wizard-checklist">
          <strong>Lease-ready checklist</strong>
          {isReady ? (
            <p className="status-text success">All required items complete.</p>
          ) : (
            <ul>
              {missingItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          <div className="lease-ready-indicator">
            <span className={`lease-ready-icon ${isReady ? 'ready' : 'not-ready'}`}>
              {isReady ? '✔' : '✕'}
            </span>
            <span>{isReady ? 'Lease‑ready' : 'Not lease‑ready yet'}</span>
          </div>
          {!isReady && <p className="subtle-text">Complete the items above to enable lease‑ready.</p>}
        </div>
      </div>
    );
  };

  if (loading) {
    return <main className="App-main">Loading form...</main>;
  }

  if (savedNeedsPublish) {
    const { propertyId, profileComplete, identityDone } = savedNeedsPublish;
    const steps = [
      { label: 'Complete your profile', done: profileComplete, link: '/landlord/analytics#profile', description: 'Add your phone, address, and date of birth.' },
      { label: 'Verify your identity', done: identityDone, link: '/landlord/dashboard/v2', description: 'Quick ID check — takes under a minute.' },
      { label: 'Publish your listing', done: false, link: `/landlord/properties/edit/${propertyId}`, description: 'Set status to Available and go live.' }
    ];
    return (
      <main className="App-main property-form-page">
        <div style={{ maxWidth: 520, margin: '2rem auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
            <h2 style={{ margin: '0 0 0.35rem' }}>Property saved!</h2>
            <p className="status-text" style={{ fontSize: '0.9rem' }}>
              Your listing is saved as a draft. Complete these steps to go live:
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {steps.map((step, i) => (
              <div key={i} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                padding: '0.85rem 1rem', borderRadius: '8px',
                border: `1px solid ${step.done ? 'var(--border-color, #e5e7eb)' : 'var(--accent-color, #28b6c8)'}`,
                background: step.done ? 'transparent' : 'var(--surface-color, #f9fafb)',
                opacity: step.done ? 0.5 : 1
              }}>
                <span style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                  {step.done ? '✅' : `${i + 1}`}
                </span>
                <strong style={{ fontSize: '0.9rem', textDecoration: step.done ? 'line-through' : 'none' }}>{step.label}</strong>
                {!step.done && <p style={{ margin: '0.2rem 0 0.5rem', fontSize: '0.82rem', color: 'var(--light-text-color)' }}>{step.description}</p>}
                {!step.done && (
                  <Link to={step.link} className="secondary-button" style={{ fontSize: '0.82rem', padding: '0.35rem 0.65rem', textDecoration: 'none', display: 'inline-block' }}>
                    {step.label.includes('Publish') ? 'Go to listing' : step.label.includes('profile') ? 'Complete profile' : 'Verify now'}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <Link to="/landlord/dashboard/v2" className="ghost-button" style={{ fontSize: '0.85rem' }}>Back to dashboard</Link>
          </div>
        </div>
      </main>
    );
  }

  if (propertyLocked) {
    return (
      <main className="App-main">
        <p className="status-text">Upgrade to Pro to edit this property on the free plan.</p>
        <Link to="/landlord/dashboard/v2" className="secondary-button">Back to dashboard</Link>
      </main>
    );
  }



  return (
    <main className="App-main property-form-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Listing</p>
          <h2>{isEditMode ? 'Edit Property' : 'Add New Property'}</h2>
          <p>Manage how this listing appears to tenants. Accurate details keep the auto‑generated lease correct while keeping setup easy.</p>
        </div>
      </div>
      {!isEditMode && subscription.subscription_status !== 'active' && propertyCount >= 2 && (
        <p className="status-text" style={{ marginBottom: '1rem' }}>
          Free plan limit reached. New listings will be saved as <strong>Off market</strong> until you upgrade.
        </p>
      )}
      <form onSubmit={handleSubmit} className="property-form">
        {error && <p className="error-message">{error}</p>}

        {!schedulingOnly && (
          <div className="lease-wizard-steps" style={{ marginTop: '1rem', marginBottom: '1.25rem' }}>
            {flowSteps.map((step, index) => {
              const stepNumber = index + 1;
              const isActive = step.key === activeFlowStep;
              return (
                <button
                  key={step.key}
                  type="button"
                  className={`lease-step ${isActive ? 'active' : ''}`}
                  onClick={() => focusFlowStep(step.key)}
                >
                  <span>{stepNumber}</span>
                  <p>{step.label}</p>
                </button>
              );
            })}
          </div>
        )}

        {!schedulingOnly && activeFlowStep === 'basics' && (
          <div className="dossier-section flat-section" style={{ marginTop: '1rem' }}>
            <div className="section-header">
              <h3>Basics</h3>
              <span className="report-chip report-chip--info">Listing</span>
            </div>
            <p className="subtle-text">Set the core details tenants see first. Keep it accurate and concise.</p>

          {isEditMode && (
            <div className="form-group">
              <label htmlFor="status">Status:</label>
              <select id="status" name="status" value={formData.status} onChange={handleChange}>
                <option value="available">Available</option>
                <option value="off_market">Off market (Draft)</option>
                <option value="occupied">Occupied</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="address">Address:</label>
            <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="unit">Unit (optional):</label>
            <input type="text" id="unit" name="unit" value={formData.unit} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="city">City:</label>
            <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="province">Province:</label>
            <input type="text" id="province" name="province" value={formData.province} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="postal_code">Postal Code:</label>
            <input type="text" id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="timezone">Property Timezone:</label>
            <select id="timezone" name="timezone" value={formData.timezone} onChange={handleChange}>
              <option value="America/Toronto">Eastern (Toronto)</option>
              <option value="America/Chicago">Central (Chicago)</option>
              <option value="America/Denver">Mountain (Denver)</option>
              <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
              <option value="America/Halifax">Atlantic (Halifax)</option>
              <option value="America/Vancouver">Pacific (Vancouver)</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="property_type">Property Type:</label>
            <input type="text" id="property_type" name="property_type" value={formData.property_type} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="bedrooms">Bedrooms:</label>
            <input type="number" id="bedrooms" name="bedrooms" value={formData.bedrooms} onChange={handleChange} min="0" />
          </div>
          <div className="form-group">
            <label htmlFor="bathrooms">Bathrooms:</label>
            <input type="number" id="bathrooms" name="bathrooms" value={formData.bathrooms} onChange={handleChange} min="0" step="0.5" />
          </div>
          <div className="form-group">
            <label htmlFor="amenities">Amenities (comma-separated):</label>
            <input type="text" id="amenities" name="amenities" value={formData.amenities} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="appliances">Appliances:</label>
            <textarea
              id="appliances"
              name="appliances"
              value={propertySettings.appliances}
              onChange={(e) => setPropertySettings((prev) => ({ ...prev, appliances: e.target.value }))}
              rows="3"
              placeholder="e.g., Fridge, stove, dishwasher"
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description:</label>
            <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows="5"></textarea>
          </div>

          {renderUpdateAction()}
          </div>
        )}


        {!schedulingOnly && activeFlowStep === 'photos' && (
          <div className="dossier-section flat-section">
            <div className="section-header">
              <h3>Photos</h3>
              <span className="report-chip report-chip--info">Media</span>
            </div>
            <p className="subtle-text">Upload clear, well‑lit photos. The first image becomes the listing cover.</p>
            <div className="form-group">
              <label htmlFor="image_upload">Add New Images:</label>
              <label htmlFor="image_upload" className="primary-button" style={{ display: 'inline-block' }}>
                Upload images
              </label>
              <input
                type="file"
                id="image_upload"
                name="images"
                onChange={handleFileChange}
                multiple
                accept="image/*"
                className="file-input-hidden"
              />
            </div>

          {uploadedImageUrls.length > 0 && (
            <div className="form-group">
              <label>Current Images (drag to reorder):</label>
              <div className="image-preview-grid">
                {uploadedImageUrls.map((url, index) => (
                  <div
                    key={index}
                    className="image-preview-container"
                    onClick={() => setSelectedImage(resolveImageUrl(url))}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(index)}
                    title="Drag to reorder"
                  >
                    {index === 0 && (
                      <span className="report-chip report-chip--success" style={{ position: 'absolute', top: '8px', left: '8px' }}>
                        Primary
                      </span>
                    )}
                    <img src={resolveImageUrl(url)} alt={`Property ${index + 1}`} className="image-preview" />
                    <div className="image-reorder-actions">
                      <button type="button" onClick={(e) => { e.stopPropagation(); moveImage(index, -1); }} aria-label="Move left">←</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); moveImage(index, 1); }} aria-label="Move right">→</button>
                    </div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveImage(url); }} className="remove-image-button" title="Remove image">&times;</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {renderUpdateAction()}
          </div>
        )}

        <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />

        {!schedulingOnly && activeFlowStep === 'details' && (
          <div className="dossier-section lease-wizard">
            <div className="section-header">
              <h3>Details</h3>
              <span className="report-chip report-chip--info">Listing</span>
            </div>
            <p className="subtle-text">These details shape what tenants see and power accurate lease generation. Most are quick toggles.</p>

            <div className="lease-wizard-body">
              <div className="lease-wizard-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0 }}>Pricing &amp; availability</h4>
                  <InfoChip label="Pricing guide">
                    Rent feeds your public listing and lease. Set the market rate (before incentives) and adjust later if offers change.
                  </InfoChip>
                </div>
                <div className="form-group">
                  <label htmlFor="rent">Rent ($/month):</label>
                  <input type="number" id="rent" name="rent" value={formData.rent} onChange={handleChange} required min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={leaseDraft.preselect.rentDeposit}
                      onChange={(e) => handleLeaseDraftChange('preselect', 'rentDeposit', e.target.checked)}
                    />
                    Last month's rent deposit
                  </label>
                  <p className="subtle-text" style={{ marginTop: '0.15rem', fontSize: '0.78rem' }}>Ontario law only permits a last month's rent deposit (no damage or security deposits).</p>
                </div>
                {leaseDraft.preselect.rentDeposit && (
                  <div className="form-group">
                    <label>Deposit amount (equal to one month's rent)</label>
                    <input type="number" min="0" step="0.01" value={leaseDraft.conditional.depositAmount} onChange={(e) => handleLeaseDraftChange('conditional', 'depositAmount', e.target.value)} placeholder="e.g., 2450" />
                  </div>
                )}
                <div className="form-group">
                  <label>Rent due day</label>
                  <select value={leaseDraft.essentials.rentDueDay} onChange={(e) => handleLeaseDraftChange('essentials', 'rentDueDay', e.target.value)}>
                    <option value="">Select</option>
                    <option value="1st">1st</option>
                    <option value="15th">15th</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tenancy type</label>
                  <select value={leaseDraft.essentials.tenancyType} onChange={(e) => handleLeaseDraftChange('essentials', 'tenancyType', e.target.value)}>
                    <option value="monthly">Monthly</option>
                    <option value="fixed">Fixed term</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Rent payable to</label>
                  <input type="text" value={leaseDraft.essentials.rentPayableTo} onChange={(e) => handleLeaseDraftChange('essentials', 'rentPayableTo', e.target.value)} placeholder="Landlord or company name" />
                </div>
                <div className="form-group">
                  <label>Payment method</label>
                  <select value={leaseDraft.preselect.paymentMethod} onChange={(e) => handleLeaseDraftChange('preselect', 'paymentMethod', e.target.value)}>
                    <option value="">Select</option>
                    <option value="credit-card">Credit card</option>
                    <option value="pad">Pre-authorized debit</option>
                    <option value="e-transfer">E-transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {renderLeaseWizardStep(1)}

              {renderLeaseWizardStep(3)}


            </div>
            {renderUpdateAction()}
          </div>
        )}

        {!schedulingOnly && activeFlowStep === 'review' && (
          <div className="dossier-section flat-section">
            <div className="section-header">
              <h3>Publish</h3>
              <span className="report-chip report-chip--info">Listing</span>
            </div>
            <p className="subtle-text">Double‑check the highlights before publishing. You can edit later.</p>

            <div className="review-summary" style={{ marginTop: '0.75rem' }}>
              <div className="review-summary-card">
                <h4>Listing summary</h4>
                {uploadedImageUrls.length > 0 && (
                  <div className="image-preview-grid" style={{ marginBottom: '0.75rem' }}>
                    {uploadedImageUrls.map((url, index) => (
                      <div key={index} className="image-preview-container">
                        <img className="image-preview" src={resolveImageUrl(url)} alt={`Listing ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                )}
                <ul>
                  <li><strong>Address:</strong> {formData.address || '—'}</li>
                  <li><strong>Unit:</strong> {formData.unit || '—'}</li>
                  <li><strong>City:</strong> {formData.city || '—'}</li>
                  <li><strong>Province:</strong> {formData.province || '—'}</li>
                  <li><strong>Postal code:</strong> {formData.postal_code || '—'}</li>
                  <li><strong>Property type:</strong> {formData.property_type || '—'}</li>
                  <li><strong>Bedrooms:</strong> {formData.bedrooms || '—'}</li>
                  <li><strong>Bathrooms:</strong> {formData.bathrooms || '—'}</li>
                  <li><strong>Rent:</strong> {formData.rent ? `$${formData.rent}` : '—'}</li>
                  <li><strong>Amenities:</strong> {formData.amenities || '—'}</li>
                  <li><strong>Description:</strong> {formData.description || '—'}</li>
                </ul>
              </div>
              <div className="review-summary-card">
                <h4>Details</h4>
                <ul>
                  <li><strong>Rent due day:</strong> {leaseDraft.essentials.rentDueDay || '—'}</li>
                  <li><strong>Tenancy type:</strong> {leaseDraft.essentials.tenancyType || '—'}</li>
                  <li><strong>Rent payable to:</strong> {leaseDraft.essentials.rentPayableTo || '—'}</li>
                  <li><strong>Payment method:</strong> {leaseDraft.preselect.paymentMethod || '—'}</li>
                  <li><strong>Last month's rent deposit:</strong> {leaseDraft.preselect.rentDeposit ? 'Yes' : 'No'}</li>
                  <li><strong>Condo unit:</strong> {leaseDraft.preselect.isCondo ? 'Yes' : 'No'}</li>
                  <li><strong>Parking included:</strong> {leaseDraft.preselect.parkingIncluded ? 'Yes' : 'No'}</li>
                  <li><strong>Utilities included:</strong> {leaseDraft.preselect.utilitiesIncluded || '—'}</li>
                  <li><strong>Laundry:</strong> {leaseDraft.preselect.laundry || '—'}</li>
                  <li><strong>Furnished:</strong> {leaseDraft.preselect.furnished ? 'Yes' : 'No'}</li>
                  <li><strong>Smoking allowed:</strong> {leaseDraft.preselect.smokingAllowed || '—'}</li>
                  <li><strong>Rent discount:</strong> {leaseDraft.preselect.rentDiscount ? 'Yes' : 'No'}</li>
                  <li><strong>Other services:</strong> {leaseDraft.preselect.otherServices ? 'Yes' : 'No'}</li>
                  <li><strong>Special terms:</strong> {leaseDraft.preselect.specialTerms ? 'Yes' : 'No'}</li>
                  <li><strong>Appliances:</strong> {propertySettings.appliances || '—'}</li>
                </ul>
              </div>
              <div className="review-summary-card">
                <h4>Lease readiness</h4>
                {renderLeaseReviewSummary()}
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '1.5rem' }}>
              <button
                type="submit"
                className="primary-button"

              >
                {isEditMode ? 'Update Listing' : 'Publish Listing'}
              </button>

              <Link to="/landlord/dashboard/v2" className="secondary-button">Cancel</Link>
            </div>
          </div>
        )}

        {(schedulingOnly || activeFlowStep === 'scheduling') && (
          <div className="dossier-section flat-section" ref={schedulingRef}>
            <div className="section-header">
              <h3>Viewings</h3>
              <span className="report-chip report-chip--info">Optional</span>
            </div>
            <p className="subtle-text" style={{ marginBottom: '0.5rem' }}>
              Viewings let prospective tenants book a time to see your property in person.
              This is optional. Tenants can always apply directly from your listing without scheduling a viewing.
            </p>

            {(showViewingForm || walkthroughSlots.length > 0) ? (
              <>
                {/* Existing slots — grouped by date */}
                {walkthroughSlots.length > 0 && (() => {
                  const grouped = walkthroughSlots.reduce((acc, slot) => {
                    const dateKey = new Date(slot.start_time).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
                    if (!acc[dateKey]) acc[dateKey] = [];
                    acc[dateKey].push(slot);
                    return acc;
                  }, {});
                  const dateKeys = Object.keys(grouped);
                  return (
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Current slots ({walkthroughSlots.length})</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        {dateKeys.map(dateKey => {
                          const slots = grouped[dateKey];
                          const isExpanded = expandedSlotDates[dateKey];
                          const openCount = slots.filter(s => !s.is_booked).length;
                          const bookedCount = slots.filter(s => s.is_booked).length;
                          return (
                            <div key={dateKey} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                              <button type="button" onClick={() => setExpandedSlotDates(prev => ({ ...prev, [dateKey]: !prev[dateKey] }))}
                                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--surface-color, #f5f8ff)', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-color)', fontWeight: 500 }}>
                                <span>{isExpanded ? '▼' : '▶'} {dateKey}</span>
                                <span className="status-text" style={{ fontSize: '0.78rem' }}>{openCount} open{bookedCount > 0 ? `, ${bookedCount} booked` : ''}</span>
                              </button>
                              {isExpanded && (
                                <div style={{ padding: '0.25rem 0.75rem 0.5rem' }}>
                                  {slots.map(slot => {
                                    const timeStr = new Date(slot.start_time).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
                                    return (
                                      <div key={slot.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.25rem 0', fontSize: '0.85rem' }}>
                                        <span>{timeStr}</span>
                                        <span className="status-text" style={{ fontSize: '0.75rem' }}>{slot.is_booked ? 'Booked' : 'Open'}</span>
                                        {!slot.is_booked && (
                                          <button type="button" className="ghost-button ghost-button--tiny" onClick={() => handleDeleteSlot(slot.id)}>Delete</button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {isEditMode && (
                  <div className="form-group">
                    <label>Add time slots</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <button type="button" className={recurringEnabled ? 'secondary-button' : 'primary-button'} style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }} onClick={() => setRecurringEnabled(false)}>Single date</button>
                      <button type="button" className={recurringEnabled ? 'primary-button' : 'secondary-button'} style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }} onClick={() => setRecurringEnabled(true)}>Weekly recurring</button>
                    </div>

                    {recurringEnabled ? (
                      <div className="walkthrough-grid-controls">
                        <label>
                          Day
                          <select value={recurringDay} onChange={(e) => setRecurringDay(e.target.value)}>
                            <option value="MON">Monday</option>
                            <option value="TUE">Tuesday</option>
                            <option value="WED">Wednesday</option>
                            <option value="THU">Thursday</option>
                            <option value="FRI">Friday</option>
                            <option value="SAT">Saturday</option>
                            <option value="SUN">Sunday</option>
                          </select>
                        </label>
                        <label>
                          Start
                          <input type="time" value={gridStartTime} onChange={(e) => setGridStartTime(e.target.value)} />
                        </label>
                        <label>
                          End
                          <input type="time" value={gridEndTime} onChange={(e) => setGridEndTime(e.target.value)} />
                        </label>
                        <label>
                          Interval
                          <select value={gridInterval} onChange={(e) => setGridInterval(Number(e.target.value))}>
                            <option value={30}>30 min</option>
                            <option value={60}>60 min</option>
                          </select>
                        </label>
                        <label>
                          Weeks
                          <select value={recurringWeeks} onChange={(e) => setRecurringWeeks(Number(e.target.value))}>
                            <option value={2}>2 weeks</option>
                            <option value={4}>4 weeks</option>
                            <option value={6}>6 weeks</option>
                            <option value={8}>8 weeks</option>
                          </select>
                        </label>
                      </div>
                    ) : (
                      <div className="walkthrough-grid-controls">
                        <label>
                          Date
                          <input type="date" value={gridDate} onChange={(e) => setGridDate(e.target.value)} />
                        </label>
                        <label>
                          Start
                          <input type="time" value={gridStartTime} onChange={(e) => setGridStartTime(e.target.value)} />
                        </label>
                        <label>
                          End
                          <input type="time" value={gridEndTime} onChange={(e) => setGridEndTime(e.target.value)} />
                        </label>
                        <label>
                          Interval
                          <select value={gridInterval} onChange={(e) => setGridInterval(Number(e.target.value))}>
                            <option value={30}>30 min</option>
                            <option value={60}>60 min</option>
                          </select>
                        </label>
                      </div>
                    )}

                    {recurringEnabled ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <button type="button" className="primary-button" style={{ fontSize: '0.85rem' }} onClick={handleAddRecurringSlots} disabled={addingRecurring}>
                          {addingRecurring ? 'Adding slots...' : `Generate ${recurringDay} slots for ${recurringWeeks} weeks`}
                        </button>
                      </div>
                    ) : (
                      <div className="walkthrough-grid">
                        {buildGridSlots().length === 0 && (
                          <p className="status-text">Set a valid time range to see the grid.</p>
                        )}
                        {buildGridSlots().map((slot) => {
                          const exists = walkthroughSlots.some((s) => s.start_time && toLocalSlotKey(s.start_time) === slot);
                          const isAdding = addingSlots.includes(slot);
                          return (
                            <button
                              key={slot}
                              type="button"
                              className={`walkthrough-slot ${exists ? 'disabled' : ''} ${isAdding ? 'selected' : ''}`}
                              onClick={() => !exists && !isAdding && handleAddGridSlot(slot)}
                              disabled={exists || isAdding}
                              title={exists ? 'Already added' : 'Click to add'}
                            >
                              {new Date(slot).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {!isEditMode && (
                  <p className="status-text" style={{ textAlign: 'center', padding: '1rem 0' }}>
                    You can add viewing time slots after publishing your listing.
                  </p>
                )}

                {renderUpdateAction()}
                {schedulingOnly && (
                  <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                    <Link to="/landlord/dashboard/v2" className="primary-button">Done</Link>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <p style={{ fontSize: '0.95rem', marginBottom: '1.25rem', color: 'var(--text-color)' }}>
                  Would you like tenants to book viewings before applying?
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button type="button" className="primary-button" onClick={() => setShowViewingForm(true)}>
                    Set up viewing availability
                  </button>
                  <button type="button" className="secondary-button" onClick={() => {
                    if (schedulingOnly) {
                      navigate('/landlord/dashboard/v2');
                    } else {
                      handleNextStep();
                    }
                  }}>
                    Skip for now
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {!schedulingOnly && activeFlowStep !== 'review' && (
          <div
            className="form-actions sticky-bottom"
            style={{
              marginTop: '2rem',
              padding: '1rem',
              background: 'var(--sticky-footer-bg, rgba(255, 255, 255, 0.9))',
              backdropFilter: 'blur(8px)',
              borderTop: '1px solid var(--border-color)',
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              marginLeft: '-1rem',
              marginRight: '-1rem',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            {flowSteps.findIndex((s) => s.key === activeFlowStep) < flowSteps.length - 1 ? (
              <button type="button" className="primary-button" onClick={handleNextStep}>
                Next: {flowSteps[flowSteps.findIndex((s) => s.key === activeFlowStep) + 1]?.label}
              </button>
            ) : (
              <button type="button" className="primary-button" onClick={() => navigate('/landlord/dashboard/v2')}>
                Done ✓
              </button>
            )}
          </div>
        )}
      </form>

    </main>
  );
}

export default PropertyForm;
